import React, { useEffect, useRef, useState } from 'react';
import { createChart, createSeriesMarkers, CandlestickSeries } from 'lightweight-charts';

const GMGN_CHAINS = ['robinhood', 'sol', 'eth', 'bsc', 'base', 'arbitrum', 'polygon'];
const INTERVALS = [
  { value: '1', label: '1分', tf: 'minute', agg: 1, barSec: 60 },
  { value: '5', label: '5分', tf: 'minute', agg: 5, barSec: 300 },
  { value: '15', label: '15分', tf: 'minute', agg: 15, barSec: 900 },
  { value: '60', label: '1小时', tf: 'hour', agg: 1, barSec: 3600 },
  { value: '240', label: '4小时', tf: 'hour', agg: 4, barSec: 14400 },
  { value: '1D', label: '1日', tf: 'day', agg: 1, barSec: 86400 },
];

const HISTORY_RANGES = [
  { value: '6h', label: '约 6 小时', sec: 6 * 3600 },
  { value: '24h', label: '约 1 天', sec: 24 * 3600 },
  { value: '3d', label: '约 3 天', sec: 3 * 86400 },
  { value: '7d', label: '约 7 天', sec: 7 * 86400 },
  { value: '30d', label: '约 30 天', sec: 30 * 86400 },
];

/** 新闻时间窗口（与 K 线周期无关）。5d/7d 上游无原生窗口，用 all + 本地按天过滤 */
const NEWS_WINDOWS = [
  { value: '24h', label: '近 24 小时', api: '24h', days: null },
  { value: '3d', label: '近 3 天', api: '72h', days: null },
  { value: '5d', label: '近 5 天', api: 'all', days: 5 },
  { value: '7d', label: '近 7 天', api: 'all', days: 7 },
  { value: 'all', label: '不限（有关键词）', api: 'all', days: null },
];

const DEFAULT_TOKEN = '0x020bfc650a365f8bb26819deaabf3e21291018b4';
const DEFAULT_CHAIN = 'robinhood';

function parseGmgnInput(raw) {
  const text = String(raw || '').trim();
  if (!text) return null;
  const urlMatch = text.match(/gmgn\.ai\/([a-z0-9_-]+)\/token\/([a-zA-Z0-9]+)/i)
    || text.match(/gmgn\.cc\/kline\/([a-z0-9_-]+)\/([a-zA-Z0-9]+)/i);
  if (urlMatch) {
    return { chain: urlMatch[1].toLowerCase(), address: urlMatch[2] };
  }
  if (/^(0x)?[a-fA-F0-9]{40}$/.test(text) || /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(text)) {
    return { address: text };
  }
  return null;
}

function newsTimeRaw(item) {
  return item?.published_at ?? item?.publish_time ?? item?.pub_time ?? item?.created_at
    ?? item?.time ?? item?.ts ?? item?.timestamp ?? item?.datetime ?? item?.date
    ?? item?.publish_at ?? item?.posted_at ?? null;
}

function newsTimeSec(item) {
  const raw = newsTimeRaw(item);
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') {
    return raw > 1e12 ? Math.floor(raw / 1000) : Math.floor(raw);
  }
  const s = String(raw).trim();
  if (/^\d{10,13}$/.test(s)) {
    const n = Number(s);
    return n > 1e12 ? Math.floor(n / 1000) : n;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor(d.getTime() / 1000);
}

function formatNewsTime(item) {
  const raw = newsTimeRaw(item);
  if (raw == null || raw === '') return '-';
  if (typeof raw === 'number' || /^\d{10,13}$/.test(String(raw).trim())) {
    const sec = newsTimeSec(item);
    if (sec == null) return String(raw);
    return new Date(sec * 1000).toLocaleString('zh-CN', { hour12: false });
  }
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime()) && /[-:T/]/.test(String(raw))) {
    return d.toLocaleString('zh-CN', { hour12: false });
  }
  return String(raw);
}

function newsTitle(item) {
  return String(
    item?.title || item?.title_cn || item?.title_en || item?.headline || item?.name || item?.summary || '新闻'
  ).slice(0, 80);
}

function newsBody(item) {
  const text = [
    item?.summary,
    item?.description,
    item?.desc,
    item?.content,
    item?.translation,
    item?.translated,
    item?.title_cn && item?.title_en && item.title_cn !== item.title_en ? item.title_en : null,
    item?.text,
    item?.body,
    item?.ai_summary,
    item?.abstract,
  ].find((v) => v != null && String(v).trim());
  return text ? String(text).trim() : '';
}

function newsSource(item) {
  return item?.source || item?.channel || item?.from || item?.source_group || item?.media || '-';
}

/** 把新闻时间对齐到最近一根 K 线时间，避免 marker 落在无蜡烛点上 */
function alignToCandle(timeSec, candleTimes, maxDiffSec = 7 * 86400) {
  if (!candleTimes.length || timeSec == null) return null;
  let best = candleTimes[0];
  let bestDiff = Math.abs(best - timeSec);
  for (const t of candleTimes) {
    const d = Math.abs(t - timeSec);
    if (d < bestDiff) {
      best = t;
      bestDiff = d;
    }
  }
  if (bestDiff > maxDiffSec) return null;
  return best;
}

function filterNewsByDays(list, days) {
  if (!days) return list;
  const cutoff = Math.floor(Date.now() / 1000) - days * 86400;
  return list.filter((item) => {
    const t = newsTimeSec(item);
    return t != null && t >= cutoff;
  });
}

function pagesForHistory(rangeSec, barSec) {
  const bars = Math.ceil(rangeSec / Math.max(barSec, 1));
  return Math.min(Math.max(Math.ceil(bars / 1000), 1), 6);
}

function formatRangeHint(fromSec, toSec, count, barSec) {
  if (!fromSec || !toSec || !count) return '';
  const from = new Date(fromSec * 1000).toLocaleString('zh-CN', { hour12: false });
  const to = new Date(toSec * 1000).toLocaleString('zh-CN', { hour12: false });
  const hours = ((toSec - fromSec) / 3600);
  const span = hours >= 48 ? `约 ${(hours / 24).toFixed(1)} 天` : `约 ${hours.toFixed(1)} 小时`;
  return `${count} 根K线 · ${span}（${from} → ${to}）`;
}

function chatHitTimeSec(item) {
  const direct = newsTimeSec(item);
  if (direct != null) return direct;
  const ctx = item?.context ?? item?.messages ?? item?.nearby;
  const list = Array.isArray(ctx) ? ctx : (Array.isArray(ctx?.messages) ? ctx.messages : []);
  for (const m of list) {
    const t = newsTimeSec(m);
    if (t != null) return t;
  }
  return null;
}

function chatHitTitle(item) {
  const ca = item?.ca_info || item?.token_info || {};
  const name = ca.name || ca.symbol || ca.token_name || '';
  const group = item?.group || item?.group_name || '';
  const sender = item?.sender || item?.username || item?.speaker || '';
  if (name && sender) return `${name} · ${sender}`;
  if (name) return String(name);
  if (sender) return `群聊提及 · ${sender}`;
  return group || '群聊 CA 命中';
}

function chatHitSnippet(item) {
  const msgs = Array.isArray(item?.context) ? item.context
    : (Array.isArray(item?.messages) ? item.messages : []);
  for (const m of msgs) {
    const t = m?.text || m?.message || m?.content;
    if (t && String(t).trim()) return String(t).trim().slice(0, 120);
  }
  return String(item?.message_text || item?.text || item?.content || '').trim().slice(0, 120);
}

function asNewsList(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  const nested = data.items || data.list || data.news || data.results || data.records;
  if (Array.isArray(nested)) return nested;
  if (Array.isArray(data.data)) return data.data;
  return [];
}

export default function MemeNewsChart({
  skillReady,
  skillGet,
  limit = 20,
  onMessage,
  loading,
  runLabeled,
}) {
  const chartRef = useRef(null);
  const chartApi = useRef(null);
  const seriesApi = useRef(null);
  const markersApi = useRef(null);

  const [chain, setChain] = useState(DEFAULT_CHAIN);
  const [tokenInput, setTokenInput] = useState(
    `https://gmgn.ai/${DEFAULT_CHAIN}/token/${DEFAULT_TOKEN}`
  );
  const [address, setAddress] = useState(DEFAULT_TOKEN);
  const [interval, setIntervalVal] = useState('15');
  const [historyRange, setHistoryRange] = useState('7d');
  const [newsWindow, setNewsWindow] = useState('7d');
  const [keyword, setKeyword] = useState('CASHCAT');
  const [tokenMeta, setTokenMeta] = useState(null);
  const [candles, setCandles] = useState([]);
  const [rangeInfo, setRangeInfo] = useState('');
  const [newsItems, setNewsItems] = useState([]);
  const [chatHits, setChatHits] = useState([]);
  const [selectedNews, setSelectedNews] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [detailTab, setDetailTab] = useState('news'); // news | chat
  const [chatLimit, setChatLimit] = useState(100);
  const [chartError, setChartError] = useState('');
  const [embedMode, setEmbedMode] = useState('annotate'); // annotate | gmgn | dex
  const [chatRangeHint, setChatRangeHint] = useState('');

  const intervalMeta = INTERVALS.find((x) => x.value === interval) || INTERVALS[2];
  const historyMeta = HISTORY_RANGES.find((x) => x.value === historyRange) || HISTORY_RANGES[3];
  const plannedPages = pagesForHistory(historyMeta.sec, intervalMeta.barSec);

  const gmgnEmbed = address
    ? `https://www.gmgn.cc/kline/${chain}/${address}?theme=light&interval=${interval}`
    : '';
  const dexEmbed = tokenMeta?.pairAddress
    ? `https://dexscreener.com/${tokenMeta.chainId || chain}/${tokenMeta.pairAddress}?embed=1&theme=light&trades=0&info=0`
    : '';

  useEffect(() => {
    if (!chartRef.current || embedMode !== 'annotate') return undefined;
    const chart = createChart(chartRef.current, {
      layout: {
        background: { color: '#ffffff' },
        textColor: '#334155',
      },
      grid: {
        vertLines: { color: '#eef2f7' },
        horzLines: { color: '#eef2f7' },
      },
      rightPriceScale: { borderColor: '#e2e8f0' },
      timeScale: { borderColor: '#e2e8f0', timeVisible: true, secondsVisible: false },
      crosshair: { mode: 1 },
      height: 420,
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#0f766e',
      downColor: '#dc2626',
      borderUpColor: '#0f766e',
      borderDownColor: '#dc2626',
      wickUpColor: '#0f766e',
      wickDownColor: '#dc2626',
    });
    chartApi.current = chart;
    seriesApi.current = series;
    const ro = new ResizeObserver(() => {
      if (!chartRef.current) return;
      chart.applyOptions({ width: chartRef.current.clientWidth });
    });
    ro.observe(chartRef.current);
    chart.applyOptions({ width: chartRef.current.clientWidth });
    return () => {
      ro.disconnect();
      chart.remove();
      chartApi.current = null;
      seriesApi.current = null;
      markersApi.current = null;
    };
  }, [embedMode]);

  useEffect(() => {
    if (!seriesApi.current || embedMode !== 'annotate') return;
    seriesApi.current.setData(candles);
    if (chartApi.current && candles.length) chartApi.current.timeScale().fitContent();
  }, [candles, embedMode]);

  useEffect(() => {
    if (!seriesApi.current || embedMode !== 'annotate') return;
    const candleTimes = candles.map((c) => c.time);
    const markers = [];
    const seenNews = new Set();
    const seenChat = new Set();

    newsItems.forEach((item, idx) => {
      const rawT = newsTimeSec(item);
      const t = alignToCandle(rawT, candleTimes);
      if (t == null || seenNews.has(t)) return;
      seenNews.add(t);
      markers.push({
        time: t,
        position: 'aboveBar',
        color: '#0f766e',
        shape: 'circle',
        text: `N${idx + 1}`,
        size: 1,
      });
    });

    chatHits.forEach((item, idx) => {
      const rawT = chatHitTimeSec(item);
      const t = alignToCandle(rawT, candleTimes);
      if (t == null || seenChat.has(t)) return;
      seenChat.add(t);
      markers.push({
        time: t,
        position: 'belowBar',
        color: '#ea580c',
        shape: 'square',
        text: `C${idx + 1}`,
        size: 1,
      });
    });

    markers.sort((a, b) => a.time - b.time || String(a.text).localeCompare(String(b.text)));
    if (markersApi.current) {
      markersApi.current.setMarkers(markers);
    } else {
      markersApi.current = createSeriesMarkers(seriesApi.current, markers);
    }
  }, [newsItems, chatHits, candles, embedMode]);

  const loadChart = async () => {
    await runLabeled('Meme K线', async () => {
      setChartError('');
      const parsed = parseGmgnInput(tokenInput) || { address: tokenInput.trim(), chain };
      const nextChain = (parsed.chain || chain || DEFAULT_CHAIN).toLowerCase();
      const nextAddr = String(parsed.address || '').trim();
      if (!nextAddr) throw new Error('请填写合约地址或粘贴 GMGN 链接');
      setChain(nextChain);
      setAddress(nextAddr);

      const resolveUrl = `/api/xxbs/meme/resolve?chain=${encodeURIComponent(nextChain)}&address=${encodeURIComponent(nextAddr)}`;
      const resolveResp = await fetch(resolveUrl, { cache: 'no-store' });
      const resolveBody = await resolveResp.json().catch(() => ({}));
      if (!resolveResp.ok || resolveBody.success === false) {
        throw new Error(resolveBody.message || `解析代币失败 ${resolveResp.status}`);
      }
      const meta = resolveBody.data || {};
      setTokenMeta(meta);
      if (meta.symbol) {
        setKeyword((prev) => (!prev || prev === 'CASHCAT' ? meta.symbol : prev));
      }

      const pool = meta.pairAddress;
      const network = meta.geckoNetwork || meta.chainId || nextChain;
      if (!pool) throw new Error('未找到交易对，无法拉 K 线');

      const pages = pagesForHistory(historyMeta.sec, intervalMeta.barSec);
      const ohlcvUrl = `/api/xxbs/meme/ohlcv?network=${encodeURIComponent(network)}&pool=${encodeURIComponent(pool)}&timeframe=${encodeURIComponent(intervalMeta.tf)}&aggregate=${encodeURIComponent(intervalMeta.agg)}&limit=1000&pages=${pages}`;
      const ohlcvResp = await fetch(ohlcvUrl, { cache: 'no-store' });
      const ohlcvBody = await ohlcvResp.json().catch(() => ({}));
      if (!ohlcvResp.ok || ohlcvBody.success === false) {
        throw new Error(ohlcvBody.message || `拉取 K 线失败 ${ohlcvResp.status}`);
      }
      const list = Array.isArray(ohlcvBody.data?.candles) ? ohlcvBody.data.candles : [];
      if (!list.length) {
        setCandles([]);
        setRangeInfo('');
        setChartError('该周期暂无 K 线数据，可换更大周期或换交易对');
        onMessage?.('K 线为空');
        return;
      }
      setCandles(list);
      const from = ohlcvBody.data?.from || list[0]?.time;
      const to = ohlcvBody.data?.to || list[list.length - 1]?.time;
      const hint = formatRangeHint(from, to, list.length, intervalMeta.barSec);
      setRangeInfo(hint);
      const wantHours = historyMeta.sec / 3600;
      const gotHours = from && to ? (to - from) / 3600 : 0;
      let extra = '';
      if (gotHours + 0.5 < wantHours * 0.7) {
        extra = `；1分钟级历史有上限，想看更久请改用 15分/1小时`;
      }
      onMessage?.(`已加载 ${meta.symbol || nextAddr.slice(0, 8)} · ${hint}${extra} · 非实时，点「加载图表」刷新`);
    });
  };

  const annotateNews = async () => {
    await runLabeled('新闻标注', async () => {
      if (!skillReady) throw new Error('请先保存闪讯 Token');
      const kw = keyword.trim() || tokenMeta?.symbol || '';
      if (!kw) throw new Error('请填写新闻关键词（如代币符号）');
      const winMeta = NEWS_WINDOWS.find((x) => x.value === newsWindow) || NEWS_WINDOWS[4];
      const apiWin = winMeta.api;
      const data = await skillGet('/crypto_news/search', {
        window: apiWin,
        lang: 'all',
        keyword: kw,
        token: kw,
        limit: Math.min(Number(limit) || 20, 50),
      });
      let list = filterNewsByDays(asNewsList(data), winMeta.days);
      list = list.slice().sort((a, b) => (newsTimeSec(b) || 0) - (newsTimeSec(a) || 0));
      setNewsItems(list);
      setSelectedNews(list[0] || null);
      setDetailTab('news');
      if (!list.length) {
        onMessage?.(`关键词「${kw}」在${winMeta.label}内无相关加密新闻，可换关键词或拉长窗口`);
      } else {
        onMessage?.(`已标注 ${list.length} 条新闻（${winMeta.label} · 关键词 ${kw}）`);
        setEmbedMode('annotate');
      }
    });
  };

  const annotateChatByCa = async () => {
    await runLabeled('群聊CA标注', async () => {
      if (!skillReady) throw new Error('请先保存闪讯 Token');
      const ca = String(tokenMeta?.tokenAddress || address || '').trim();
      if (!ca) throw new Error('请先加载图表，确认合约地址');
      const take = Math.min(Math.max(Number(chatLimit) || 100, 1), 100);
      const data = await skillGet('/chat_records/search', {
        type: 'ca',
        query: ca,
        limit: take,
      });
      let list = asNewsList(data);
      list = list.slice().sort((a, b) => (chatHitTimeSec(b) || 0) - (chatHitTimeSec(a) || 0));
      setChatHits(list);
      setSelectedChat(list[0] || null);
      setDetailTab('chat');

      const timed = list.map(chatHitTimeSec).filter((t) => t != null);
      if (timed.length) {
        const newest = Math.max(...timed);
        const oldest = Math.min(...timed);
        const hint = `接口只返回「最新 ${take} 条」命中（无更早翻页）。本次 ${list.length} 条时间跨度：${new Date(oldest * 1000).toLocaleString('zh-CN', { hour12: false })} → ${new Date(newest * 1000).toLocaleString('zh-CN', { hour12: false })}。若最旧停在某天，说明更早命中被条数截断，不是那天之后才有数据。`;
        setChatRangeHint(hint);
      } else {
        setChatRangeHint(`已返回 ${list.length} 条（上限 ${take}），但缺少可用时间戳，部分无法上图。`);
      }

      if (!list.length) {
        onMessage?.(`合约 ${ca.slice(0, 10)}… 在群聊记录中未命中`);
      } else {
        const marked = timed.length;
        onMessage?.(`群聊 CA 最新 ${list.length}/${take} 条（可上图 ${marked}）。停在某日=条数截断，不是历史结束。`);
        setEmbedMode('annotate');
      }
    });
  };

  useEffect(() => {
    loadChart().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="global-info-panel meme-chart-panel">
      <div className="global-info-panel-head">
        <div>
          <h2>Meme K 线 + 新闻/群聊标注</h2>
          <p>
            非实时推送：点「加载图表」拉 K 线快照。可用关键词标新闻（绿 N），或按当前合约搜群聊命中（橙 C）叠到图上。
          </p>
        </div>
        <div className="meme-chart-head-actions">
          <button type="button" className="global-info-btn soft" disabled={!!loading} onClick={loadChart}>加载图表</button>
          <button type="button" className="global-info-btn soft" disabled={!skillReady || !!loading} onClick={annotateNews}>
            生成新闻标注
          </button>
          <button type="button" className="global-info-btn" disabled={!skillReady || !!loading || !address} onClick={annotateChatByCa}>
            按合约标群聊
          </button>
        </div>
      </div>

      <div className="global-info-filters">
        <label className="meme-chart-grow">
          代币（GMGN 链接或合约地址）
          <input
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="粘贴 gmgn.ai/.../token/0x... 或合约地址"
          />
        </label>
        <label>
          链
          <select value={chain} onChange={(e) => setChain(e.target.value)}>
            {GMGN_CHAINS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>
          K线周期
          <select value={interval} onChange={(e) => setIntervalVal(e.target.value)}>
            {INTERVALS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
          </select>
        </label>
        <label>
          历史跨度
          <select value={historyRange} onChange={(e) => setHistoryRange(e.target.value)}>
            {HISTORY_RANGES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
          </select>
        </label>
        <label>
          新闻窗口
          <select value={newsWindow} onChange={(e) => setNewsWindow(e.target.value)}>
            {NEWS_WINDOWS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
          </select>
        </label>
        <label>
          新闻关键词
          <input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="CASHCAT / 代币名" />
        </label>
        <label>
          群聊条数
          <select value={chatLimit} onChange={(e) => setChatLimit(Number(e.target.value))}>
            <option value={20}>最新 20</option>
            <option value={50}>最新 50</option>
            <option value={100}>最新 100</option>
          </select>
        </label>
      </div>

      {tokenMeta && (
        <div className="meme-chart-meta">
          <strong>{tokenMeta.symbol || '-'}</strong>
          <span>{tokenMeta.name || ''}</span>
          <span>{tokenMeta.chainId || chain}</span>
          <span>{tokenMeta.priceUsd != null ? `$${Number(tokenMeta.priceUsd).toPrecision(4)}` : ''}</span>
          <span className="meme-chart-range-pill">计划 {plannedPages} 页×1000 · {historyMeta.label} / {intervalMeta.label}</span>
          {tokenMeta.url && (
            <a href={tokenMeta.url} target="_blank" rel="noreferrer">DexScreener</a>
          )}
          {address && (
            <a href={`https://gmgn.ai/${chain}/token/${address}`} target="_blank" rel="noreferrer">GMGN</a>
          )}
        </div>
      )}
      {rangeInfo && <div className="meme-chart-range-info">已加载：{rangeInfo} · 可拖拽/滚轮缩放时间轴</div>}

      <div className="meme-chart-tabs">
        <button type="button" className={embedMode === 'annotate' ? 'active' : ''} onClick={() => setEmbedMode('annotate')}>可标注 K 线</button>
        <button type="button" className={embedMode === 'gmgn' ? 'active' : ''} onClick={() => setEmbedMode('gmgn')}>GMGN 原图</button>
        <button type="button" className={embedMode === 'dex' ? 'active' : ''} onClick={() => setEmbedMode('dex')} disabled={!dexEmbed}>DexScreener</button>
      </div>

      {embedMode === 'annotate' && (
        <div className="meme-chart-stage">
          {chartError && <div className="global-info-error">{chartError}</div>}
          <div ref={chartRef} className="meme-chart-canvas" />
          <p className="meme-chart-hint">
            绿色圆点 N = 新闻；橙色方块 C = 群聊 CA 命中。非实时，刷新请点「加载图表」。
          </p>
        </div>
      )}

      {embedMode === 'gmgn' && (
        <div className="meme-chart-embed-wrap">
          {gmgnEmbed ? (
            <iframe title="GMGN K线" src={gmgnEmbed} className="meme-chart-embed" loading="lazy" referrerPolicy="no-referrer" />
          ) : (
            <div className="global-info-empty">请先填写代币地址</div>
          )}
          <p className="meme-chart-hint">GMGN 官方嵌入；若被站点拦截显示空白，请用「可标注 K 线」或 DexScreener。</p>
        </div>
      )}

      {embedMode === 'dex' && (
        <div className="meme-chart-embed-wrap">
          {dexEmbed ? (
            <iframe title="DexScreener" src={dexEmbed} className="meme-chart-embed" loading="lazy" />
          ) : (
            <div className="global-info-empty">请先加载图表解析交易对</div>
          )}
        </div>
      )}

      <div className="meme-anno-tabs">
        <button type="button" className={detailTab === 'news' ? 'active' : ''} onClick={() => setDetailTab('news')}>
          新闻标注 N · {newsItems.length}
        </button>
        <button type="button" className={detailTab === 'chat' ? 'active' : ''} onClick={() => setDetailTab('chat')}>
          群聊 CA · {chatHits.length}
        </button>
      </div>

      {detailTab === 'news' ? (
        <div className="meme-news-split">
          <div>
            <div className="global-info-usage-subhead">
              <h4>新闻时间线</h4>
              <span>{newsItems.length} 条</span>
            </div>
            {newsItems.length === 0 ? (
              <div className="global-info-empty soft">填写关键词后点「生成新闻标注」</div>
            ) : (
              <div className="meme-news-list">
                {newsItems.map((item, idx) => {
                  const href = item.url || item.link || item.source_url;
                  const active = selectedNews === item;
                  return (
                    <button
                      type="button"
                      key={item.id || href || idx}
                      className={`meme-news-item ${active ? 'active' : ''}`}
                      onClick={() => setSelectedNews(item)}
                    >
                      <em>N{idx + 1}</em>
                      <div>
                        <strong>{newsTitle(item)}</strong>
                        <small>
                          {newsSource(item)}
                          {' · '}
                          {formatNewsTime(item)}
                        </small>
                      </div>
                      {href ? <a href={href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>原文</a> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="meme-news-detail">
            <div className="global-info-usage-subhead">
              <h4>当前新闻</h4>
            </div>
            {!selectedNews ? (
              <div className="global-info-empty soft">选择左侧一条新闻查看</div>
            ) : (
              <article>
                <h3>{newsTitle(selectedNews)}</h3>
                <p>{newsBody(selectedNews) || '上游未返回摘要字段，可点「原文」查看全文。'}</p>
                <small>
                  {newsSource(selectedNews)}
                  {' · '}
                  {formatNewsTime(selectedNews)}
                </small>
              </article>
            )}
          </div>
        </div>
      ) : (
        <div className="meme-news-split">
          <div>
            <div className="global-info-usage-subhead">
              <h4>群聊 CA 时间线</h4>
              <span>{chatHits.length} 条</span>
            </div>
            {chatRangeHint && <div className="meme-chat-range-hint">{chatRangeHint}</div>}
            {chatHits.length === 0 ? (
              <div className="global-info-empty soft">
                先加载图表拿到合约，再点「按合约标群聊」。上游只返回最新 N 条命中（可选 20/50/100），没有按日期翻页。
              </div>
            ) : (
              <div className="meme-news-list">
                {chatHits.map((item, idx) => {
                  const active = selectedChat === item;
                  const t = chatHitTimeSec(item);
                  return (
                    <button
                      type="button"
                      key={item.id || `${item.query}-${idx}`}
                      className={`meme-news-item chat ${active ? 'active' : ''}`}
                      onClick={() => setSelectedChat(item)}
                    >
                      <em className="chat-mark">C{idx + 1}</em>
                      <div>
                        <strong>{chatHitTitle(item)}</strong>
                        <small>
                          {item.group || item.group_name || '-'}
                          {' · '}
                          {t ? new Date(t * 1000).toLocaleString('zh-CN', { hour12: false }) : '无时间戳'}
                        </small>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="meme-news-detail">
            <div className="global-info-usage-subhead">
              <h4>当前群聊命中</h4>
            </div>
            {!selectedChat ? (
              <div className="global-info-empty soft">选择左侧一条群聊记录查看</div>
            ) : (
              <article>
                <h3>{chatHitTitle(selectedChat)}</h3>
                <p>{chatHitSnippet(selectedChat) || '暂无对话摘要'}</p>
                <small>
                  {(selectedChat.group || selectedChat.group_name || '-')}
                  {' · '}
                  {(selectedChat.sender || selectedChat.username || '-')}
                  {' · '}
                  {(() => {
                    const t = chatHitTimeSec(selectedChat);
                    return t ? new Date(t * 1000).toLocaleString('zh-CN', { hour12: false }) : '-';
                  })()}
                </small>
                {(selectedChat.query || selectedChat.ca) && (
                  <p className="meme-chat-ca-line">
                    CA：<code>{String(selectedChat.query || selectedChat.ca)}</code>
                  </p>
                )}
                {Array.isArray(selectedChat.context) && selectedChat.context.length > 0 && (
                  <div className="meme-chat-ctx">
                    {selectedChat.context.slice(0, 6).map((m, i) => (
                      <div key={i} className="meme-chat-ctx-row">
                        <strong>{m.speaker || m.username || '用户'}</strong>
                        <span>{m.text || m.message || ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
